import { Button, Input, Modal, Select, Typography, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	ApplicationsFilter,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApplicationStatusTranslation } from '../../../hooks/useApplicationStatusTranslation';
import {
	ApplicationStatus,
	Gender,
	ReviewAction,
	VALID_ABHYASI_ID_REGEX,
} from '../../../shared/Constants';
import ZoneDropdown from '../../../components/ZonesDropdown';
import { useAuth } from '../../../hooks/useAuth';
import { isProposerPartCompleted } from '../../../shared/Utils';
import { useRedirectToLogin } from '../../../hooks/useRedirectToLogin';
import moment from 'moment';
import { useSnapshot } from 'valtio';
import { BatchInfoState } from '../../../components/BatchInfo.store';
import { UndoOutlined } from '@ant-design/icons';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import { ApplicationsListState } from './ApplicationsList.store';
interface FilterProps {
	value: ApplicationsFilter;
	onFilterChange: (value: ApplicationsFilter) => void;
}

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const Filter = ({
	value: {
		status,
		searchByApplicantName,
		gender,
		zoneNames,
		applicantSrcmId,
		assignedToLevel1FunctionarySrcmId,
		preceptorReview = [],
		proposerReview = [],
		searchByProposerName,
	},
	onFilterChange,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage =
		i18n.language === 'ta' ||
		i18n.language === 'ma' ||
		i18n.language === 'te' ||
		i18n.language === 'ka';
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const { getTranslatedValue } = useApplicationStatusTranslation();
	const { srcmId } = useAuth();
	const { role } = useRedirectToLogin();
	const batchStateSnap = useSnapshot(BatchInfoState);
	const batchInfo = batchStateSnap.batchInfo;
	const [statusValue, setStatusValue] = useState<string | undefined>(
		status && status.length > 0 ? status[0] : undefined
	);
	const [preceptorRecommendationValue, setPreceptorRecommendationValue] =
		useState<Array<string>>(preceptorReview);
	const [proposerRecommendationValue, setProposerRecommendationValue] =
		useState<Array<string>>(proposerReview);
	const [nameValue, setNameValue] = useState<string | undefined>(
		searchByApplicantName
	);
	// ProposerName Value I added new
	const [proposerNameValue, setProposerNameValue] = useState<
		string | undefined
	>(searchByProposerName);
	const [genderValue, setGenderValue] = useState<string | undefined>(gender);
	const [zones, setZones] = useState<Array<string> | undefined>(zoneNames);
	const [applicantSrcmIdValue, setApplicantSrcmId] = useState<
		string | undefined
	>(applicantSrcmId);
	const [proposerSrcmId, setProposerSrcmId] = useState<string | undefined>(
		assignedToLevel1FunctionarySrcmId
	);
	const [showStatusFilter, setShowStatusFilter] = useState<boolean>(true);
	const [showFilterByZone, setShowFilterByZone] = useState<boolean>(true);
	const [showFilterByProposerSRCMId, setShowFilterByProposerSRCMId] =
		useState<boolean>(true);
	const [isFilterPopup, setIsFilterPopup] = useState<boolean>(false);
	const [isFilterTextApplied, setIsFilterTextApplied] =
		useState<boolean>(false);
	const [isReset, setIsReset] = useState(false);
	const [applicantSrcmIdError, setApplicantSrcmIdError] = useState('');
	const [proposerSrcmIdError, setProposerSrcmIdError] = useState('');
	const showProposerFields = role !== UserRole.FUNCTIONARY;

	const filterStateSnap = useSnapshot(ApplicationsListState);
	useEffect(() => {
		const stateFilter = { ...filterStateSnap.filterBy } as ApplicationsFilter;
		delete stateFilter.status;
		const isStateFilterEmpty = Object.entries(stateFilter).every(
			([key, value]) =>
				key === 'gender'
					? value === 'NOT_PROVIDED'
					: Array.isArray(value)
					? value.length === 0
					: value === ''
		);
		if (!isStateFilterEmpty) {
			setIsFilterTextApplied(true);
		} else {
			setIsFilterTextApplied(false);
		}
	}, [filterStateSnap.filterBy]);

	const isZCWithValidDate =
		role === UserRole.ZC && batchInfo?.snpResultDateForZC;

	const isDefaultDate =
		batchInfo?.snpResultDateForZC === '1970-01-01T00:00:00Z';
	const isBeforeOrOnDate = moment().isSameOrBefore(
		batchInfo?.snpResultDateForZC
	);

	const showFilter =
		role === UserRole.CARE_TEAM ||
		(isZCWithValidDate && (isDefaultDate || isBeforeOrOnDate));

	useEffect(() => {
		if (showFilter) {
			setShowStatusFilter(false);
			setShowFilterByProposerSRCMId(false);
		}
		if (role === UserRole.CARE_TEAM) {
			setShowFilterByZone(false);
		}
	}, [role, showFilter]);

	const statusFilterOptions = useMemo(() => {
		if (
			(role === UserRole.ZC &&
				batchInfo?.snpResultDateForZC &&
				batchInfo.snpResultDateForZC !== '1970-01-01T00:00:00Z' &&
				moment().isSameOrAfter(batchInfo?.snpResultDateForZC)) ||
			(role === UserRole.APPROVER &&
				batchInfo?.snpResultDateForApprover &&
				batchInfo.snpResultDateForApprover !== '1970-01-01T00:00:00Z' &&
				moment().isSameOrAfter(batchInfo?.snpResultDateForApprover))
		) {
			return [
				{
					value: ApplicationStatus.APPROVED,
					label: getTranslatedValue(ApplicationStatus.APPROVED),
				},
				{
					value: ApplicationStatus.REJECTED,
					label: getTranslatedValue(ApplicationStatus.REJECTED),
				},
			];
		} else {
			return [
				{
					value: ApplicationStatus.USER_SUBMITTED_APPLICATION,
					label: getTranslatedValue(
						ApplicationStatus.USER_SUBMITTED_APPLICATION
					),
				},
				{
					value: ApplicationStatus.FUNCTIONARY_ASSIGNED,
					label: getTranslatedValue(ApplicationStatus.FUNCTIONARY_ASSIGNED),
				},
				{
					value: ApplicationStatus.FUNCTIONARY_SUBMITTED,
					label: getTranslatedValue(ApplicationStatus.FUNCTIONARY_SUBMITTED),
				},
				{
					value: ApplicationStatus.WAITING_FOR_CARE_TEAM,
					label: getTranslatedValue(ApplicationStatus.WAITING_FOR_CARE_TEAM),
				},
				{
					value: ApplicationStatus.APPROVED,
					label: getTranslatedValue(ApplicationStatus.APPROVED),
				},
				{
					value: ApplicationStatus.REJECTED,
					label: getTranslatedValue(ApplicationStatus.REJECTED),
				},
			];
		}
	}, [
		getTranslatedValue,
		batchInfo?.snpResultDateForZC,
		batchInfo?.snpResultDateForApprover,
		role,
	]);

	const genderDropdownOptions = useMemo(() => {
		return [
			{
				value: Gender.NOT_PROVIDED,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('bothGenders')}
					</p>
				),
			},
			{
				value: Gender.MALE,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('male')}
					</p>
				),
			},
			{
				value: Gender.FEMALE,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('female')}
					</p>
				),
			},
		];
	}, [t]);

	const recommendationDropdownOptions = useMemo(() => {
		return [
			{
				value: ReviewAction.APPROVE,
				label: t('approve'),
			},
			{
				value: ReviewAction.REJECT,
				label: t('notGoingForward'),
			},
		];
	}, [t]);

	const handleGenderChange = useCallback((value: string) => {
		setGenderValue(value);
	}, []);

	const handlePreceptorRecommendationChange = useCallback(
		(value: Array<string>) => {
			setPreceptorRecommendationValue(value);
		},
		[]
	);

	const handleProposerRecommendationChange = useCallback(
		(value: Array<string>) => {
			setProposerRecommendationValue(value);
		},
		[]
	);

	const handleZoneChange = useCallback((value: Array<{ value: string }>) => {
		setZones(value.map((z) => z.value));
	}, []);

	const fireFilterChange = useCallback(
		(updatedStatusValue?: string) => {
			// We need to proposer key value here which I added new
			let statusFilter = [updatedStatusValue ?? (statusValue as string)];
			if (role === UserRole.CARE_TEAM) {
				statusFilter = [
					ApplicationStatus.FUNCTIONARY_ASSIGNED,
					ApplicationStatus.FUNCTIONARY_SUBMITTED,
					ApplicationStatus.WAITING_FOR_CARE_TEAM,
					ApplicationStatus.APPROVED,
				];
			}
			onFilterChange({
				status: statusFilter,
				applicantSrcmId: applicantSrcmIdValue,
				searchByApplicantName: nameValue,
				assignedToLevel1FunctionarySrcmId: proposerSrcmId,
				gender: genderValue,
				zoneNames: zones,
				preceptorReview: preceptorRecommendationValue,
				proposerReview: proposerRecommendationValue,
				searchByProposerName: proposerNameValue,
			});
		},
		[
			applicantSrcmIdValue,
			genderValue,
			nameValue,
			onFilterChange,
			preceptorRecommendationValue,
			proposerRecommendationValue,
			proposerSrcmId,
			statusValue,
			zones,
			proposerNameValue,
			role,
		]
	);

	useEffect(() => {
		if (isReset) {
			fireFilterChange(statusValue);
			setIsReset(false);
		}
	}, [isReset, statusValue, fireFilterChange]);

	const handleFilterButtonClick = useCallback(() => {
		fireFilterChange();
		setIsFilterPopup(false);
		setIsFilterTextApplied(true);
	}, [fireFilterChange]);

	const showRecommendationFilter = useMemo(() => {
		return isProposerPartCompleted(statusValue) && role !== UserRole.CARE_TEAM;
	}, [role, statusValue]);

	const handleResetFilters = useCallback(() => {
		setGenderValue('NOT_PROVIDED');
		setNameValue('');
		setProposerNameValue('');
		setProposerSrcmId('');
		setProposerSrcmIdError('');
		setZones([]);
		setApplicantSrcmId('');
		setApplicantSrcmIdError('');
		if (showRecommendationFilter) {
			setPreceptorRecommendationValue([]);
			setProposerRecommendationValue([]);
		}
		setIsReset(true);
		onFilterChange({});

		setIsFilterPopup(false);
		setIsFilterTextApplied(false);
	}, [showRecommendationFilter, onFilterChange]);

	const handleStatusChange = useCallback(
		(value: string) => {
			if (showRecommendationFilter) {
				setPreceptorRecommendationValue([]);
				setProposerRecommendationValue([]);
			}
			handleResetFilters();

			setStatusValue(value);
			onFilterChange({
				status: [value ?? (statusValue as string)],
			});
		},
		[handleResetFilters, showRecommendationFilter, onFilterChange, statusValue]
	);

	const onApplicantNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setNameValue(e.target.value);
		},
		[]
	);

	const onProposerNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setProposerNameValue(e.target.value);
		},
		[]
	);

	const onApplicantSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setApplicantSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setApplicantSrcmIdError('invalidAbhyasiId');
			} else {
				setApplicantSrcmIdError('');
			}
		},
		[]
	);

	const onProposerSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setProposerSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setProposerSrcmIdError('invalidAbhyasiId');
			} else {
				setProposerSrcmIdError('');
			}
		},
		[]
	);

	const handleFilterPopup = useCallback(() => {
		setIsFilterPopup(true);
	}, []);

	const onClose = useCallback(() => {
		setIsFilterPopup(false);
	}, []);

	return (
		<>
			<div className="w-full mb-4">
				<Text>{t('filterApplications')}</Text>
			</div>
			<div className="flex flex-row h-full min-h-0 gap-2">
				{showStatusFilter && (
					<div className="flex flex-col  gap-2 mb-[8px] w-5/6 sm:w-auto">
						<Select
							className="flex min-w-[200px]"
							value={statusValue}
							options={statusFilterOptions}
							onChange={handleStatusChange}
							popupMatchSelectWidth={false}
							disabled={!showProposerFields}
						/>
					</div>
				)}

				<div className="flex justify-end w-1/6 sm:w-auto">
					<Button
						className="block sm:hidden"
						variant="outlined"
						onClick={handleFilterPopup}
					>
						<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
					</Button>
					<Button
						className={`hidden sm:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5 ${
							isFilterTextApplied ? 'border-primary-blue' : 'border-gray-200'
						}`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterTextApplied
							? t('advancedFilterApplied')
							: t('advancedFilter')}
					</Button>

					{isFilterTextApplied ? (
						<Button
							className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
				</div>
				<Modal
					open={isFilterPopup}
					onCancel={onClose}
					footer={null}
					centered
					className={
						isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
					}
					closable={true}
					maskClosable={true}
				>
					<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
						{t('advancedFilter')}
					</div>

					<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
						{showFilterByZone && (
							<div className="w-full sm:w-[calc(50%-0.5rem)]">
								<div className="mb-[16px] flex flex-col gap-2">
									<Text className="block text-xs text-gray-650">
										{t('filterByZone')}
									</Text>
									<ZoneDropdown
										functionarySrcmId={srcmId}
										value={zones}
										onChange={handleZoneChange}
									/>
								</div>
							</div>
						)}

						{showRecommendationFilter && (
							<>
								<div className="w-full sm:w-[calc(50%-0.5rem)]">
									<div className="mb-[16px] flex flex-col gap-2">
										<Text className="text-xs text-gray-650">
											{t('proposerRecommendation')}
										</Text>
										<Select
											className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
											mode="multiple"
											value={proposerRecommendationValue}
											options={recommendationDropdownOptions}
											onChange={handleProposerRecommendationChange}
											placeholder={t('pleaseSelect')}
										/>
									</div>
								</div>
								<div className="w-full sm:w-[calc(50%-0.5rem)]">
									<div className="mb-[16px] flex flex-col gap-2">
										<Text className="text-xs text-gray-650">
											{t('preceptorRecommendation')}
										</Text>
										<Select
											mode="multiple"
											className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
											value={preceptorRecommendationValue}
											options={recommendationDropdownOptions}
											onChange={handlePreceptorRecommendationChange}
											placeholder={t('pleaseSelect')}
										/>
									</div>
								</div>
							</>
						)}
						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByGender')}
								</Text>
								<Select
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={genderValue ?? 'NOT_PROVIDED'}
									options={genderDropdownOptions}
									onChange={handleGenderChange}
								/>
							</div>
						</div>
						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByApplicantName')}
								</Text>
								<Input
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={nameValue}
									allowClear
									onChange={onApplicantNameChange}
								/>
							</div>
						</div>
						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByApplicantSRCMId')}
								</Text>
								<Input
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={applicantSrcmIdValue}
									allowClear
									onChange={onApplicantSRCMIdChange}
								/>
								{applicantSrcmIdError && (
									<Text className="text-xs text-red-500">
										{t(applicantSrcmIdError)}
									</Text>
								)}
							</div>
						</div>

						{showProposerFields && (
							<div className="w-full sm:w-[calc(50%-0.5rem)]">
								<div className="mb-[16px] flex flex-col gap-2">
									<Text className="text-xs text-gray-650">
										{t('filterByProposerName')}
									</Text>
									<Input
										className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
										value={proposerNameValue}
										allowClear
										onChange={onProposerNameChange}
									/>
								</div>
							</div>
						)}
						{showProposerFields && showFilterByProposerSRCMId && (
							<div className="w-full sm:w-[calc(50%-0.5rem)]">
								<div className="mb-[16px] flex flex-col gap-2">
									<Text className="text-xs text-gray-650">
										{t('filterByProposerSRCMId')}
									</Text>
									<Input
										className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
										value={proposerSrcmId}
										allowClear
										onChange={onProposerSRCMIdChange}
									/>
									{proposerSrcmIdError && (
										<Text className="text-xs text-red-500">
											{t(proposerSrcmIdError)}
										</Text>
									)}
								</div>
							</div>
						)}
					</div>
					<div></div>
					<div className="flex justify-center">
						<Button
							className="block sm:hidden flex-1 h-9 text-primary-blue font-sans font-bold text-center"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}

								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					</div>
					<div
						className={`flex ${
							isStackVertical
								? 'flex-col items-center space-y-2'
								: 'justify-center gap-2'
						} mt-4 ant-modal-footer`}
					>
						<Button
							className="hidden sm:block flex-1 h-9 text-primary-blue font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
						<Button
							className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
								isStackVertical ? 'p-2' : ''
							}`}
							onClick={onClose}
						>
							{t('closeFilter')}
						</Button>
						<Button
							type="primary"
							onClick={handleFilterButtonClick}
							className={`flex-1 h-9 font-sans font-bold ${
								isStackVertical ? 'p-2 mt-3' : ''
							}`}
							disabled={
								proposerSrcmIdError !== '' || applicantSrcmIdError !== ''
							}
						>
							{t('applyFilter')}
						</Button>
					</div>
				</Modal>
			</div>
		</>
	);
};
